import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import AppShell from "../shell/AppShell.jsx";
import DetailDrawer from "../components/DetailDrawer.jsx";
import WorkerSearchDialog, { toSelectedWorker } from "./WorkerSearchDialog.jsx";
// MapLibre GL is ~1MB of the bundle and is only ever needed once an
// operator actually opens a route on this one page, so it is code-split
// out of the main chunk rather than shipped to every screen in the app.
const RouteMap = lazy(() => import("../components/RouteMap.jsx"));
import { listWorkerProfiles } from "../workerProfiles/workerProfilesApi.js";
import { useBackofficeIdentity } from "../hierarchy/BackofficeIdentityContext.jsx";
import { useFootstepHistory } from "./useFootstepHistory.js";
import { useFootstepAccessLogs, FOOTSTEP_ACCESS_LOG_DEFAULT_PAGE_SIZE } from "./useFootstepAccessLogs.js";
import { getWarRoomPersonDetail } from "../warRoom/warRoomApi.js";
import "./footstepHistory.css";
import "../pages/states.css";
import Pagination from "../components/Pagination.jsx";

import { DetailSkeleton, MediaSkeleton } from "../components/Skeleton.jsx";
const CRUMBS = ["Backoffice", "Tracking", "Footstep History"];
const DRAWER_TITLE_ID = "fsh-drawer-title";

const SAMPLE_MODE_OPTIONS = ["Raw", "Simplified"];
const MAX_POINTS_CEILING = 2000;
const SETTINGS_DEBOUNCE_MS = 350;

/**
 * The worker this screen opens on so an operator lands on a real route
 * instead of an empty picker. Resolved through the real worker-profiles
 * search API on mount - never a hardcoded worker object - so if the code
 * stops existing or falls outside the caller's scope the page simply falls
 * back to the normal "choose a worker" empty state.
 */
const DEFAULT_WORKER_CODE = "p140dwrk10240";

const SUBTITLE_TEXT = "เลือกแรงงานและวันที่เพื่อดูเส้นทางการเดินทางย้อนหลัง";

const DEFAULT_PURPOSE = "ตรวจสอบประวัติเส้นทางแรงงานตามสิทธิ์ของผู้ดูแลระบบ";

const POLICY_TOOLTIP =
  "ต้องระบุเหตุผลก่อนเปิดดูประวัติ · ระบบบันทึกผู้เปิดดู ช่วงเวลา และจำนวนจุด แต่ไม่บันทึกรายการพิกัดทั้งหมดใน audit log";

const ACCESS_LOG_RESTRICTED_TEXT = "หน้านี้จำกัดเฉพาะ SuperAdmin สำหรับตรวจสอบ compliance การเปิดดู Footstep History";
const ACCESS_LOG_NO_COORDINATE_TITLE = "หน้านี้ไม่แสดงพิกัดใด ๆ";
const ACCESS_LOG_NO_COORDINATE_LINE_1 = "แสดงเฉพาะผู้เปิดดู ช่วงเวลา จำนวนจุด และเหตุผลเท่านั้น";
const ACCESS_LOG_NO_COORDINATE_LINE_2 = "ไม่มีรายการพิกัด (latitude/longitude) ปรากฏในหน้านี้";

const ACCESS_LOG_ABOUT_TOOLTIP =
  "แสดงว่าใครเปิดดู Footstep ของใคร เมื่อไร และเพราะอะไร · จำกัดเฉพาะ SuperAdmin เพื่อการตรวจสอบ compliance · " +
  "ไม่มีพิกัดหรือรายการจุดใด ๆ ปรากฏในหน้านี้";

const DRAWER_SELECTABLE_SELECTOR = ".wx-fsh-advanced-button, .wx-fsh-controlbar-summary-button";

/**
 * P144-F: the map layer modes. All three read the SAME real points[] for
 * the selected worker and day - "Heatmap" is a different rendering of that
 * one worker's own day, never an aggregate across workers (this backend has
 * no multi-worker coordinate endpoint, and this page never invents one).
 */
const MAP_MODES = [
  { value: "route", label: "เส้นทาง" },
  { value: "heatmap", label: "Heatmap" },
  { value: "both", label: "ทั้งคู่" },
];

/** Long opaque ids (actor/worker) are truncated head+tail so a table row never becomes an unreadable wall of hex. */
function truncateId(value, headLength = 8, tailLength = 4) {
  if (!value) {
    return "-";
  }
  const text = String(value);
  if (text.length <= headLength + tailLength + 1) {
    return text;
  }
  return `${text.slice(0, headLength)}…${text.slice(-tailLength)}`;
}

const ICT_TIME_ZONE = "Asia/Bangkok";

/**
 * P144-F: the backend serializes DATETIME2 columns without a timezone
 * designator (e.g. "2026-07-31T01:05:00"), and those values are UTC by
 * contract. Plain `new Date(value)` would parse them as BROWSER-LOCAL time,
 * so a point captured 08:05 ICT rendered as "01:05 ICT" on a UTC host - a
 * real, wrong-by-7-hours label. Normalizing to an explicit UTC instant here,
 * and formatting every timestamp with an explicit Asia/Bangkok timeZone
 * below, is what makes the "ICT" suffix on this screen actually true
 * regardless of where the operator's machine is.
 */
function parseServerDate(value) {
  if (!value) {
    return null;
  }
  const normalized = typeof value === "string" && !/(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTimeTh(value) {
  const date = parseServerDate(value);
  if (!date) {
    return null;
  }
  const datePart = date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric", timeZone: ICT_TIME_ZONE });
  const timePart = date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: ICT_TIME_ZONE });
  return `${datePart} · ${timePart} ICT`;
}

function formatTimeOnly(value) {
  const date = parseServerDate(value);
  if (!date) {
    return "-";
  }
  return date.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: ICT_TIME_ZONE });
}

/** Real elapsed time between the first and last returned point - never a fabricated/estimated value, so no "ระยะเวลา" is shown unless both timestamps are real. */
function formatDurationTh(startValue, endValue) {
  const start = parseServerDate(startValue);
  const end = parseServerDate(endValue);
  if (!start || !end) {
    return null;
  }
  const totalMinutes = Math.max(0, Math.round((end.getTime() - start.getTime()) / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return `${minutes} นาที`;
  }
  if (minutes === 0) {
    return `${hours} ชม.`;
  }
  return `${hours} ชม. ${minutes} นาที`;
}

function formatDateLabelTh(dateStr) {
  const date = new Date(`${dateStr}T00:00:00+07:00`);
  if (Number.isNaN(date.getTime())) {
    return dateStr;
  }
  return date.toLocaleDateString("th-TH-u-ca-gregory", { day: "2-digit", month: "short", year: "numeric" });
}

/** "Today" as an ICT calendar date (YYYY-MM-DD) - this app's real users/shifts are all Thailand-local, never the browser/server's own timezone. */
function todayIct() {
  const shifted = new Date(Date.now() + 7 * 3600 * 1000);
  return shifted.toISOString().slice(0, 10);
}

/** A selected calendar date -> the real UTC instant range for that ICT day (00:00:00 to 23:59:59 ICT). */
function dayBoundsIct(dateStr) {
  const fromUtc = new Date(`${dateStr}T00:00:00+07:00`).toISOString();
  const toUtc = new Date(`${dateStr}T23:59:59+07:00`).toISOString();
  return { fromUtc, toUtc };
}

const STAY_RADIUS_METERS = 70;

/** Great-circle distance in metres - used only to group points, never to invent a value shown to the user. */
function distanceMeters(a, b) {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadius = 6371000;
  const dLat = toRad(Number(b.latitude) - Number(a.latitude));
  const dLng = toRad(Number(b.longitude) - Number(a.longitude));
  const lat1 = toRad(Number(a.latitude));
  const lat2 = toRad(Number(b.latitude));
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * earthRadius * Math.asin(Math.sqrt(h));
}

/**
 * P144-P: groups consecutive real points into "อยู่ที่เดิม" (stay) and
 * "เดินทาง" (move) segments so a 26-point day reads as a handful of
 * meaningful blocks instead of 26 near-identical rows. Pure grouping over
 * the real points - no coordinate, time or count is synthesised, and every
 * segment still exposes its own underlying points on expand.
 */
function buildSegments(points) {
  if (points.length === 0) {
    return [];
  }
  if (points.length === 1) {
    return [{ kind: "move", points: [points[0]] }];
  }

  // Classify each STEP (not each point) by how far it moved from the fix
  // before it. Comparing against the previous point rather than a segment
  // anchor is what keeps a long walk from collapsing into one block: an
  // anchor-based test stays "far" forever once you leave, so every later
  // fix lands in the same segment.
  const stepKind = (index) => (distanceMeters(points[index - 1], points[index]) <= STAY_RADIUS_METERS ? "stay" : "move");

  const segments = [];
  let currentKind = stepKind(1);
  let current = [points[0]];

  for (let index = 1; index < points.length; index += 1) {
    const kind = stepKind(index);
    if (kind === currentKind) {
      current.push(points[index]);
      continue;
    }
    if (kind === "stay") {
      // The previous fix is where standing still actually began, so it
      // belongs to the stay rather than to the travel leg before it.
      const seed = current.length > 1 ? current.pop() : null;
      if (current.length > 0) {
        segments.push({ kind: currentKind, points: current });
      }
      current = seed ? [seed, points[index]] : [points[index]];
    } else {
      segments.push({ kind: currentKind, points: current });
      current = [points[index]];
    }
    currentKind = kind;
  }
  segments.push({ kind: currentKind, points: current });

  return segments;
}

function describeHistoryError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เปิดดู Footstep History ของแรงงานรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบแรงงานที่เลือก อาจถูกลบหรือย้ายแล้ว";
  }
  if (error?.status === 400) {
    return "ข้อมูลที่ใช้ค้นหาไม่ถูกต้อง กรุณาตรวจสอบวันที่หรือตัวเลือกขั้นสูงอีกครั้ง";
  }
  return "ไม่สามารถโหลด Footstep History ได้ กรุณาลองใหม่อีกครั้ง";
}

function describeSummaryError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return "คุณไม่มีสิทธิ์เปิดดูสรุป Footstep ของแรงงานรายนี้";
  }
  if (error?.status === 404) {
    return "ไม่พบแรงงานที่เลือก";
  }
  return "ไม่สามารถโหลดสรุปได้ กรุณาลองใหม่อีกครั้ง";
}

function describeAccessLogsError(error) {
  if (error?.status === 401) {
    return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  }
  if (error?.status === 403) {
    return ACCESS_LOG_RESTRICTED_TEXT;
  }
  return "ไม่สามารถโหลดรายการ Access Log ได้ กรุณาลองใหม่อีกครั้ง";
}

function validateSettings({ maxPoints, purpose }) {
  const errors = [];
  if (!purpose.trim()) {
    errors.push("กรุณาระบุเหตุผลในการเปิดดู (Purpose) ก่อนเปิดดู");
  }
  if (maxPoints !== "" && maxPoints !== null && maxPoints !== undefined) {
    const numeric = Number(maxPoints);
    if (!Number.isInteger(numeric) || numeric < 1 || numeric > MAX_POINTS_CEILING) {
      errors.push(`Max Points ต้องเป็นจำนวนเต็มระหว่าง 1 ถึง ${MAX_POINTS_CEILING}`);
    }
  }
  return errors;
}

/**
 * /backoffice/tracking/footsteps - the Backoffice Footstep History screen
 * (Prompt 143 triage rebuild of the original Prompt 115B/W55 form). Same
 * real, purpose-logged Prompt 114 API underneath (GET .../footsteps,
 * .../footsteps/summary, .../footstep-access-logs) - only the workflow
 * changed: pick a real worker from a real search dialog (never type a raw
 * Worker ID), pick one real calendar day, and see the real returned points
 * plotted on a real Google Maps route instead of a raw table. P141R17
 * removed Leaflet/OpenStreetMap from this page entirely; P144-F added the
 * Route/Heatmap/Both layer switch over those same real points.
 * Assignment ID/Sample Mode/Max Points/Purpose move into a secondary
 * "Advanced / เหตุผลการเข้าถึง" drawer - still real, still required by the
 * backend (a missing purpose still 400s), just no longer the first thing an
 * operator has to fill in. This screen still never evaluates a geofence,
 * scores a location as fake, or touches face recognition/SOS/export -
 * canExport/canEvaluateGeofence stay reserved, mentioned only in the
 * compact "เกี่ยวกับข้อมูลนี้" footer note, never as a headline banner.
 */
export default function FootstepHistoryPage() {
  const { data: identity } = useBackofficeIdentity();
  const primaryRole = identity?.primaryRole;

  const [activeTab, setActiveTab] = useState("history");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [defaultWorkerResolved, setDefaultWorkerResolved] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => todayIct());

  const [assignmentIdInput, setAssignmentIdInput] = useState("");
  const [sampleMode, setSampleMode] = useState(SAMPLE_MODE_OPTIONS[0]);
  const [maxPointsInput, setMaxPointsInput] = useState("");
  const [purposeInput, setPurposeInput] = useState(DEFAULT_PURPOSE);
  const [settingsDraft, setSettingsDraft] = useState({ assignmentId: "", sampleMode: SAMPLE_MODE_OPTIONS[0], maxPoints: "", purpose: DEFAULT_PURPOSE });
  const [validationErrors, setValidationErrors] = useState([]);

  const [activePointId, setActivePointId] = useState(null);
  const [mapMode, setMapMode] = useState("route");
  const [drawerView, setDrawerView] = useState(null);

  // P144-R: this worker's own canonical safety status (same event-backed
  // source War Room uses - GET .../war-room/workers/{personId}), reused here
  // only to show an honest badge near the map. Never labeled "SOS" unless
  // sourceType is genuinely "Sos" - a stale/missing heartbeat with no real
  // open event is Warning, not an emergency.
  const [workerStatus, setWorkerStatus] = useState(null);
  useEffect(() => {
    if (!selectedWorker) {
      setWorkerStatus(null);
      return undefined;
    }
    let cancelled = false;
    getWarRoomPersonDetail(selectedWorker.workerId, "WorkerProfile")
      .then((detail) => {
        if (!cancelled) {
          setWorkerStatus(detail);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWorkerStatus(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedWorker?.workerId]);

  // Resolve the default worker once, from the real search API, and only
  // when the operator has not already chosen someone.
  useEffect(() => {
    let cancelled = false;
    listWorkerProfiles({ search: DEFAULT_WORKER_CODE, page: 1, pageSize: 1 })
      .then((response) => {
        const match = (response?.items ?? []).find((item) => item.workerCode === DEFAULT_WORKER_CODE);
        if (!cancelled && match) {
          setSelectedWorker((current) => current ?? toSelectedWorker(match));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          setDefaultWorkerResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const { historyState, summaryState, runHistoryQuery, runSummaryQuery, resetSummary, resetHistory } = useFootstepHistory();

  // Settings (assignmentId/sampleMode/maxPoints/purpose) live in the
  // Advanced drawer and debounce into the real query the same way the
  // search box elsewhere in this app debounces (350ms) - so a query never
  // fires on every single keystroke inside the drawer's text fields.
  useEffect(() => {
    const timer = setTimeout(
      () => setSettingsDraft({ assignmentId: assignmentIdInput.trim(), sampleMode, maxPoints: maxPointsInput, purpose: purposeInput }),
      SETTINGS_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [assignmentIdInput, sampleMode, maxPointsInput, purposeInput]);

  const runQuery = useCallback(
    (worker, dateStr, settings) => {
      const errors = validateSettings(settings);
      setValidationErrors(errors);
      if (errors.length > 0) {
        return;
      }
      const { fromUtc, toUtc } = dayBoundsIct(dateStr);
      setActivePointId(null);
      runHistoryQuery(worker.workerId, {
        fromUtc,
        toUtc,
        assignmentId: settings.assignmentId || "",
        sampleMode: settings.sampleMode,
        maxPoints: settings.maxPoints,
        purpose: settings.purpose.trim(),
      }).catch(() => {});
    },
    [runHistoryQuery]
  );

  useEffect(() => {
    if (!selectedWorker) {
      resetHistory();
      return;
    }
    // A native <input type="date"> emits "" while its value is being
    // cleared (e.g. mid-edit) - never a partial string, but always guard
    // against the empty case rather than letting dayBoundsIct throw.
    if (!/^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      return;
    }
    runQuery(selectedWorker, selectedDate, settingsDraft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorker?.workerId, selectedDate, settingsDraft]);

  function handlePickWorker(worker) {
    setSelectedWorker(worker);
    setDialogOpen(false);
  }

  function handleRefresh() {
    if (!selectedWorker) {
      return;
    }
    runQuery(selectedWorker, selectedDate, settingsDraft);
  }

  const handleOpenSummary = useCallback(() => {
    if (!selectedWorker) {
      return;
    }
    setDrawerView("summary");
    const { fromUtc, toUtc } = dayBoundsIct(selectedDate);
    runSummaryQuery(selectedWorker.workerId, { fromUtc, toUtc, assignmentId: settingsDraft.assignmentId || "" }).catch(() => {});
  }, [selectedWorker, selectedDate, settingsDraft.assignmentId, runSummaryQuery]);

  function closeDrawer() {
    setDrawerView(null);
    if (drawerView === "summary") {
      resetSummary();
    }
  }

  const historyData = historyState.data;
  const points = historyData?.points ?? [];

  const accessLogFilters = useMemo(
    () => ({ workerId: "", actorUserId: "", fromUtc: "", toUtc: "", page: 1, pageSize: FOOTSTEP_ACCESS_LOG_DEFAULT_PAGE_SIZE }),
    []
  );
  const [accessPage, setAccessPage] = useState(1);
  const [accessFilters, setAccessFilters] = useState(accessLogFilters);
  useEffect(() => setAccessFilters((previous) => ({ ...previous, page: accessPage })), [accessPage]);

  const {
    status: accessLogStatus,
    data: accessLogData,
    error: accessLogError,
    refetch: refetchAccessLogs,
  } = useFootstepAccessLogs(accessFilters, { enabled: activeTab === "access-logs" });

  return (
    <AppShell crumbs={CRUMBS}>
      <div className="wx-fsh-page">
        <header className="wx-fsh-head">
          <div className="wx-fsh-title">
            <h1>Footstep History</h1>
            <p>{SUBTITLE_TEXT}</p>
          </div>
          {primaryRole && (
            <span className="wx-fsh-scope-pill">
              Scope <b>{primaryRole}</b>
            </span>
          )}
        </header>

        <div className="wx-fsh-view-toggle" role="tablist" aria-label="มุมมอง Footstep">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "history"}
            className={`wx-fsh-view-toggle-btn${activeTab === "history" ? " wx-fsh-view-toggle-btn--active" : ""}`}
            onClick={() => setActiveTab("history")}
          >
            Footstep History
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "access-logs"}
            className={`wx-fsh-view-toggle-btn${activeTab === "access-logs" ? " wx-fsh-view-toggle-btn--active" : ""}`}
            onClick={() => setActiveTab("access-logs")}
          >
            Access Logs (Compliance)
          </button>
        </div>

        {activeTab === "history" ? (
          <FootstepHistoryView
            selectedWorker={selectedWorker}
            onOpenDialog={() => setDialogOpen(true)}
            onChangeWorker={() => setDialogOpen(true)}
            selectedDate={selectedDate}
            onChangeDate={setSelectedDate}
            onRefresh={handleRefresh}
            onOpenAdvanced={() => setDrawerView("advanced")}
            onOpenSummary={handleOpenSummary}
            historyState={historyState}
            points={points}
            activePointId={activePointId}
            onSelectPoint={setActivePointId}
            workerStatus={workerStatus}
            mapMode={mapMode}
            onChangeMapMode={setMapMode}
            defaultWorkerResolved={defaultWorkerResolved}
          />
        ) : (
          <FootstepAccessLogsView
            page={accessPage}
            setPage={setAccessPage}
            status={accessLogStatus}
            data={accessLogData}
            error={accessLogError}
            onRefresh={refetchAccessLogs}
          />
        )}
      </div>

      <WorkerSearchDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSelect={handlePickWorker} />

      <DetailDrawer open={drawerView !== null} onClose={closeDrawer} labelledBy={DRAWER_TITLE_ID} selectableSelector={DRAWER_SELECTABLE_SELECTOR}>
        {drawerView === "advanced" && (
          <AdvancedSettingsView
            titleId={DRAWER_TITLE_ID}
            assignmentIdInput={assignmentIdInput}
            setAssignmentIdInput={setAssignmentIdInput}
            sampleMode={sampleMode}
            setSampleMode={setSampleMode}
            maxPointsInput={maxPointsInput}
            setMaxPointsInput={setMaxPointsInput}
            purposeInput={purposeInput}
            setPurposeInput={setPurposeInput}
            validationErrors={validationErrors}
          />
        )}
        {drawerView === "summary" && <FootstepSummaryDrawerView titleId={DRAWER_TITLE_ID} state={summaryState} />}
      </DetailDrawer>
    </AppShell>
  );
}

function FootstepHistoryView({
  selectedWorker,
  onOpenDialog,
  onChangeWorker,
  selectedDate,
  onChangeDate,
  onRefresh,
  onOpenAdvanced,
  onOpenSummary,
  historyState,
  points,
  activePointId,
  onSelectPoint,
  workerStatus,
  mapMode,
  onChangeMapMode,
  defaultWorkerResolved,
}) {
  const isLoading = historyState.status === "loading";
  const data = historyState.data;
  const dayLabel = formatDateLabelTh(selectedDate);

  const firstPoint = points[0] ?? null;
  const lastPoint = points.length > 0 ? points[points.length - 1] : null;
  const startTimeLabel = firstPoint ? formatTimeOnly(firstPoint.capturedAtUtc) : null;
  const endTimeLabel = points.length > 1 && lastPoint ? formatTimeOnly(lastPoint.capturedAtUtc) : null;
  const durationLabel = points.length > 1 ? formatDurationTh(firstPoint.capturedAtUtc, lastPoint.capturedAtUtc) : null;

  return (
    <>
      <div className="wx-fsh-controlbar">
        {selectedWorker ? (
          <div className="wx-fsh-worker-chip">
            <div className="wx-fsh-worker-chip-name">
              <strong>{selectedWorker.name}</strong>
              <span>{selectedWorker.workerCode}</span>
            </div>
            <button type="button" className="wx-fsh-worker-chip-change" onClick={onChangeWorker}>
              เปลี่ยน
            </button>
          </div>
        ) : (
          <div className="wx-fsh-worker-slot-empty">
            <span className="wx-fsh-worker-slot-empty-dot" aria-hidden="true" />
            ยังไม่ได้เลือกแรงงาน
          </div>
        )}

        <div className="wx-fsh-date-field">
          <label htmlFor="fsh-date-input">วันที่</label>
          <input id="fsh-date-input" type="date" value={selectedDate} onChange={(event) => onChangeDate(event.target.value)} />
        </div>
        <span className="wx-fsh-date-label">{formatDateLabelTh(selectedDate)}</span>

        <div className="wx-fsh-controlbar-actions">
          <button type="button" className="wx-button wx-button--ghost wx-fsh-controlbar-summary-button" onClick={onOpenSummary} disabled={!selectedWorker}>
            สรุปช่วงเวลา
          </button>
          <button type="button" className="wx-button wx-button--ghost" onClick={onRefresh} disabled={!selectedWorker || isLoading}>
            Refresh
          </button>
          <button type="button" className="wx-button wx-button--ghost wx-fsh-advanced-button" onClick={onOpenAdvanced}>
            Advanced
          </button>
        </div>
      </div>

      {selectedWorker && (
        <div className="wx-fsh-worker-summary">
          <div className="wx-fsh-worker-summary-item">
            <label>แรงงาน</label>
            <strong>{data?.workerName ?? selectedWorker.name}</strong>
          </div>
          <div className="wx-fsh-worker-summary-item">
            <label>รหัส</label>
            <strong>{data?.workerCode ?? selectedWorker.workerCode}</strong>
          </div>
          <div className="wx-fsh-worker-summary-item">
            <label>Agent</label>
            <strong>{selectedWorker.agentCode ?? "-"} · {selectedWorker.agentName ?? "-"}</strong>
          </div>
          <div className="wx-fsh-worker-summary-item">
            <label>สถานะ</label>
            <strong>{selectedWorker.workerStatus ?? "-"}</strong>
          </div>
          {data?.pointCount != null && (
            <div className="wx-fsh-worker-summary-item wx-fsh-worker-summary-item--num">
              <label>จุดที่พบ</label>
              <strong>{data.pointCount} จุด</strong>
            </div>
          )}
          {startTimeLabel && (
            <div className="wx-fsh-worker-summary-item wx-fsh-worker-summary-item--num">
              <label>เริ่มต้น</label>
              <strong>{startTimeLabel} ICT</strong>
            </div>
          )}
          {endTimeLabel && (
            <div className="wx-fsh-worker-summary-item wx-fsh-worker-summary-item--num">
              <label>สิ้นสุด</label>
              <strong>{endTimeLabel} ICT</strong>
            </div>
          )}
          {durationLabel && (
            <div className="wx-fsh-worker-summary-item wx-fsh-worker-summary-item--num">
              <label>ระยะเวลา</label>
              <strong>{durationLabel}</strong>
            </div>
          )}
          {workerStatus && (workerStatus.statusCategory === "abnormal" || workerStatus.statusCategory === "warning") && (
            <div className="wx-fsh-worker-summary-item">
              <label>ความปลอดภัย</label>
              <span className={`wx-badge wx-badge--${workerStatus.statusCategory === "abnormal" ? "danger" : "warning"}`}>
                {workerStatus.sourceType === "Sos" ? "SOS" : workerStatus.reasonLabel}
              </span>
            </div>
          )}
        </div>
      )}

      {!selectedWorker && !defaultWorkerResolved ? (
        <MediaSkeleton label="กำลังเปิดข้อมูลแรงงานเริ่มต้น..." height={420} />
      ) : !selectedWorker ? (
        <div className="wx-fsh-placeholder">
          <div className="wx-fsh-placeholder-icon" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
          </div>
          <strong>เลือกแรงงานและวันที่เพื่อเริ่มดูเส้นทาง</strong>
          <span>ค้นหาแรงงานจากระบบจริง แล้วดูเส้นทางย้อนหลังพร้อมไทม์ไลน์ได้ในหน้าจอเดียว</span>
          <button type="button" className="wx-button wx-button--primary wx-fsh-placeholder-cta" onClick={onOpenDialog}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            เลือกแรงงาน
          </button>
        </div>
      ) : historyState.status === "error" ? (
        <div className="wx-fsh-placeholder">
          <strong>ไม่สามารถเปิดดู Footstep History ได้</strong>
          <span>{describeHistoryError(historyState.error)}</span>
        </div>
      ) : (
        <div className="wx-fsh-main">
          <div className="wx-fsh-map-col">
            <div className="wx-fsh-map-bar">
              <div className="wx-fsh-map-bar-title">
                <strong>เส้นทางวันที่ {dayLabel}</strong>
                <span>{points.length > 0 ? `${points.length} จุด` : "ไม่มีจุดพิกัด"}</span>
              </div>
              <div className="wx-fsh-map-modes" role="group" aria-label="รูปแบบแผนที่">
                {MAP_MODES.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={mapMode === option.value}
                    className={`wx-fsh-map-mode-btn${mapMode === option.value ? " wx-fsh-map-mode-btn--active" : ""}`}
                    onClick={() => onChangeMapMode(option.value)}
                    disabled={points.length === 0}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <Suspense fallback={<div className="wx-rmap-frame wx-rmap-empty"><p>กำลังโหลดแผนที่...</p></div>}>
              <RouteMap points={points} activePointId={activePointId} onSelectPoint={onSelectPoint} mode={mapMode} />
            </Suspense>
            {points.length === 0 && historyState.status !== "loading" && (
              <div className="wx-fsh-noday">
                <strong>ไม่มี footstep ของวันที่ {dayLabel}</strong>
                <span>แรงงานรายนี้ไม่มีการบันทึกพิกัดในวันดังกล่าว ลองเลือกวันอื่น หรือเปลี่ยนไปดูแรงงานคนอื่น</span>
                <div className="wx-fsh-noday-actions">
                  <button type="button" className="wx-button wx-button--ghost" onClick={() => onChangeDate(todayIct())}>
                    ดูวันนี้
                  </button>
                  <button type="button" className="wx-button wx-button--primary" onClick={onChangeWorker}>
                    เปลี่ยนแรงงาน
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="wx-fsh-timeline-col">
            <div className="wx-fsh-timeline-head">
              <div className="wx-fsh-timeline-head-text">
                <strong>ไทม์ไลน์</strong>
                <span>{dayLabel}</span>
              </div>
              <span className="wx-fsh-timeline-count">{points.length} จุด</span>
            </div>

            {points.length === 0 && historyState.status !== "loading" ? (
              <p className="wx-fsh-empty-list">ยังไม่มีจุดพิกัดให้แสดงในวันนี้</p>
            ) : (
              <div className="wx-fsh-timeline-list">
                {buildSegments(points).map((segment) => {
                  const first = segment.points[0];
                  const last = segment.points[segment.points.length - 1];
                  const startIndex = points.indexOf(first);
                  const isStay = segment.kind === "stay";
                  const duration = formatDurationTh(first.capturedAtUtc, last.capturedAtUtc);
                  const containsActive = segment.points.some((point) => point.locationEventId === activePointId);

                  return (
                    <section
                      key={first.locationEventId ?? startIndex}
                      className={`wx-fsh-seg wx-fsh-seg--${segment.kind}${containsActive ? " wx-fsh-seg--active" : ""}`}
                    >
                      <header className="wx-fsh-seg-head">
                        <span className="wx-fsh-seg-kind">{isStay ? "อยู่ที่เดิม" : "เดินทาง"}</span>
                        <span className="wx-fsh-seg-time">
                          {formatTimeOnly(first.capturedAtUtc)}
                          {segment.points.length > 1 && ` - ${formatTimeOnly(last.capturedAtUtc)}`}
                        </span>
                        {duration && segment.points.length > 1 && <span className="wx-fsh-seg-duration">{duration}</span>}
                      </header>

                      <div className="wx-fsh-seg-points">
                        {segment.points.map((point) => {
                          const index = points.indexOf(point);
                          const isFirstOfDay = index === 0;
                          const isLastOfDay = index === points.length - 1;
                          const isActive = point.locationEventId === activePointId;
                          return (
                            <button
                              type="button"
                              key={point.locationEventId || index}
                              className={`wx-fsh-timeline-row${isActive ? " wx-fsh-timeline-row--active" : ""}`}
                              onClick={() => onSelectPoint(point.locationEventId)}
                            >
                              <span
                                className={`wx-fsh-timeline-marker${isFirstOfDay ? " wx-fsh-timeline-marker--start" : isLastOfDay ? " wx-fsh-timeline-marker--end" : ""}`}
                                aria-hidden="true"
                              />
                              <span className="wx-fsh-timeline-row-top">
                                <strong>{formatTimeOnly(point.capturedAtUtc)}</strong>
                                <span className="wx-fsh-date-label">
                                  {isFirstOfDay ? "เริ่มต้น" : isLastOfDay ? "สิ้นสุด" : `#${index + 1}`}
                                </span>
                              </span>
                              <span className="wx-fsh-timeline-row-meta">
                                <span>
                                  {Number(point.latitude).toFixed(4)}, {Number(point.longitude).toFixed(4)}
                                </span>
                                {point.accuracyMeters != null && <span>± {Number(point.accuracyMeters).toFixed(0)} ม.</span>}
                                {point.provider && <span>{point.provider}</span>}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      <footer className="wx-fsh-about-footer">
        <span className="wx-fsh-about" tabIndex={0} title={POLICY_TOOLTIP}>
          เกี่ยวกับข้อมูลนี้
        </span>
      </footer>
    </>
  );
}

function AdvancedSettingsView({ titleId, assignmentIdInput, setAssignmentIdInput, sampleMode, setSampleMode, maxPointsInput, setMaxPointsInput, purposeInput, setPurposeInput, validationErrors }) {
  return (
    <div className="wx-fsh-drawer">
      <span className="wx-fsh-drawer-kicker">ADVANCED</span>
      <h2 id={titleId} className="wx-fsh-drawer-title">
        ตัวเลือกขั้นสูง / เหตุผลการเข้าถึง
      </h2>
      <p className="wx-fsh-drawer-sub">ค่าเหล่านี้ไม่บังคับสำหรับการใช้งานทั่วไป ยกเว้นเหตุผลในการเปิดดู (Purpose) ซึ่งระบบกำหนดให้ระบุเสมอ</p>

      <section className="wx-fsh-dsec">
        <label className="wx-fsh-field">
          <span>
            เหตุผลในการเปิดดู (Purpose) <b>*</b>
          </span>
          <textarea
            aria-label="เหตุผลในการเปิดดู (Purpose)"
            value={purposeInput}
            onChange={(event) => setPurposeInput(event.target.value)}
          />
        </label>

        <label className="wx-fsh-field">
          <span>Assignment ID (ไม่บังคับ)</span>
          <input type="text" aria-label="Assignment ID" value={assignmentIdInput} onChange={(event) => setAssignmentIdInput(event.target.value)} />
        </label>

        <label className="wx-fsh-field">
          <span>Sample Mode</span>
          <select aria-label="Sample Mode" value={sampleMode} onChange={(event) => setSampleMode(event.target.value)}>
            {SAMPLE_MODE_OPTIONS.map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </select>
        </label>

        <label className="wx-fsh-field">
          <span>Max Points (ไม่บังคับ)</span>
          <input
            type="number"
            min="1"
            max={MAX_POINTS_CEILING}
            placeholder="500"
            aria-label="Max Points"
            value={maxPointsInput}
            onChange={(event) => setMaxPointsInput(event.target.value)}
          />
        </label>

        {validationErrors.length > 0 && (
          <div className="wx-fsh-validation-errors" role="alert">
            {validationErrors.map((message) => (
              <span key={message}>{message}</span>
            ))}
          </div>
        )}
      </section>

      <section className="wx-fsh-dsec">
        <h3>เกี่ยวกับข้อมูลนี้</h3>
        <p className="wx-fsh-note-muted wx-fsh-note-muted--left">ข้อมูล Footstep เป็นข้อมูลอ่อนไหว ทุกการเปิดดูถูกบันทึกผู้เปิดดู ช่วงเวลา และจำนวนจุด (ไม่บันทึกพิกัดทั้งหมด) ใน audit log</p>
        {/* Two notes were here: one naming the canExport/canEvaluateGeofence
            permission flags, and one printing the two endpoint URLs this page
            reads. Neither is a fact about the worker being looked at. */}
      </section>
    </div>
  );
}

function FootstepSummaryDrawerView({ titleId, state }) {
  if (state.status === "loading" || state.status === "idle") {
    return (
      <DetailSkeleton label="กำลังโหลดสรุป..." />
    );
  }

  if (state.status === "error") {
    return (
      <div className="wx-fsh-drawer-state" role="alert">
        <h2 id={titleId} className="wx-state-title">
          ไม่สามารถโหลดสรุปได้
        </h2>
        <p className="wx-state-body">{describeSummaryError(state.error)}</p>
      </div>
    );
  }

  const summary = state.data;
  const totalPoints = summary.totalPoints ?? 0;

  return (
    <div className="wx-fsh-drawer">
      <span className="wx-fsh-drawer-kicker">SUMMARY</span>
      <h2 id={titleId} className="wx-fsh-drawer-title">
        สรุปช่วงเวลา
      </h2>
      <p className="wx-fsh-drawer-sub">
        {formatDateTimeTh(summary.fromUtc) ?? "-"} - {formatDateTimeTh(summary.toUtc) ?? "-"}
      </p>
      <div className="wx-fsh-drawer-status">
        <span className={`wx-badge ${summary.footstepHistoryAvailable ? "wx-badge--success" : "wx-badge--neutral"}`}>
          footstepHistoryAvailable: {String(Boolean(summary.footstepHistoryAvailable))}
        </span>
      </div>

      <section className="wx-fsh-dsec">
        <div className="wx-fsh-kv">
          <span>จำนวนจุดทั้งหมด</span>
          <strong>{summary.totalPoints ?? 0}</strong>
        </div>
        <div className="wx-fsh-kv">
          <span>จุดแรกเมื่อ</span>
          <strong>{formatDateTimeTh(summary.firstPointAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-fsh-kv">
          <span>จุดสุดท้ายเมื่อ</span>
          <strong>{formatDateTimeTh(summary.lastPointAtUtc) ?? "-"}</strong>
        </div>
        <div className="wx-fsh-kv">
          <span>จุดที่มาจากแอปปลอมพิกัด</span>
          <strong>{summary.mockProviderPointCount ?? 0}</strong>
        </div>
        <div className="wx-fsh-kv">
          <span>จุดที่ความแม่นยำต่ำ</span>
          <strong>{summary.lowAccuracyPointCount ?? 0}</strong>
        </div>
      </section>

      <section className="wx-fsh-dsec">
        <h3>Provider Breakdown</h3>
        {summary.providerBreakdown && summary.providerBreakdown.length > 0 ? (
          <div className="wx-fsh-provider-bars">
            {summary.providerBreakdown.map((entry) => {
              const percent = totalPoints > 0 ? Math.round((entry.count / totalPoints) * 100) : 0;
              return (
                <div className="wx-fsh-provider-bar-row" key={entry.provider}>
                  <span>{entry.provider}</span>
                  <div className="wx-fsh-provider-bar-track">
                    <div className="wx-fsh-provider-bar-fill" style={{ width: `${percent}%` }} />
                  </div>
                  <span>{entry.count}</span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="wx-fsh-note-muted wx-fsh-note-muted--left">ไม่มีข้อมูล provider ในช่วงเวลานี้</p>
        )}
      </section>
    </div>
  );
}

function FootstepAccessLogsView({ page, setPage, status, data, error, onRefresh }) {
  const isRestricted = status === "error" && error?.status === 403;
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / FOOTSTEP_ACCESS_LOG_DEFAULT_PAGE_SIZE));

  return (
    <>
      <div className="wx-fsh-privacy-banner" role="note">
        <div className="wx-fsh-privacy-title">
          <span className="wx-fsh-privacy-dot" aria-hidden="true" />
          {ACCESS_LOG_NO_COORDINATE_TITLE}
        </div>
        <ul>
          <li>{ACCESS_LOG_NO_COORDINATE_LINE_1}</li>
          <li>{ACCESS_LOG_NO_COORDINATE_LINE_2}</li>
        </ul>
      </div>

      {isRestricted ? (
        <section className="wx-panel">
          <div className="wx-fsh-state-inline">
            <strong>ไม่มีสิทธิ์เข้าถึงหน้านี้</strong>
            <span>{ACCESS_LOG_RESTRICTED_TEXT}</span>
          </div>
        </section>
      ) : (
        <section className="wx-panel">
          <div className="wx-fsh-panel-head">
            <h2 className="wx-panel-title">รายการการเข้าดู Footstep</h2>
            <span className="wx-fsh-panel-head-meta">{totalCount} รายการ</span>
          </div>

          <div className="wx-fsh-access-filters">
            <button type="button" className="wx-button wx-button--ghost" onClick={onRefresh}>
              Refresh
            </button>
          </div>

          {status === "error" && !isRestricted && (
            <p className="wx-fsh-inline-error" role="alert">
              {describeAccessLogsError(error)}
            </p>
          )}

          {status !== "loading" && items.length === 0 ? (
            <p className="wx-fsh-empty-list">ยังไม่มีการเข้าดู Footstep ในขอบเขตที่มองเห็น</p>
          ) : (
            <>
              <div className="wx-fsh-table-scroll">
                <table className="wx-fsh-table wx-fsh-table--access-log">
                  <thead>
                    <tr>
                      <th>Accessed At (UTC)</th>
                      <th>Actor</th>
                      <th>Worker</th>
                      <th>Purpose</th>
                      <th>Range (UTC)</th>
                      <th>Points</th>
                      <th>Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <strong>{formatDateTimeTh(entry.accessedAtUtc) ?? "-"}</strong>
                        </td>
                        <td>
                          <span className="wx-fsh-id-cell" title={entry.actorUserId ?? ""}>
                            {truncateId(entry.actorUserId)}
                          </span>
                        </td>
                        <td>
                          <span className="wx-fsh-id-cell" title={entry.workerId ?? ""}>
                            {truncateId(entry.workerId)}
                          </span>
                        </td>
                        <td>
                          <div className="wx-fsh-purpose-cell" title={entry.accessPurpose ?? ""}>
                            {entry.accessPurpose ? entry.accessPurpose : <span className="wx-fsh-reserved-text">ไม่บังคับสำหรับ Summary</span>}
                          </div>
                        </td>
                        <td>
                          {formatDateTimeTh(entry.fromUtc) ?? "-"} - {formatDateTimeTh(entry.toUtc) ?? "-"}
                        </td>
                        <td>{entry.pointCount ?? "-"}</td>
                        <td>
                          <span className={`wx-badge ${entry.accessType === "Summary" ? "wx-badge--neutral" : "wx-badge--info"}`}>
                            {entry.accessType ?? "-"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="wx-fsh-cards">
                {items.map((entry) => (
                  <div key={entry.id} className="wx-fsh-card">
                    <div className="wx-fsh-card-top">
                      <strong className="wx-fsh-id-cell" title={entry.workerId ?? ""}>
                        {truncateId(entry.workerId)}
                      </strong>
                      <span className={`wx-badge ${entry.accessType === "Summary" ? "wx-badge--neutral" : "wx-badge--info"}`}>
                        {entry.accessType ?? "-"}
                      </span>
                    </div>
                    <div className="wx-fsh-card-grid">
                      <div>
                        <label>Accessed At</label>
                        <strong>{formatDateTimeTh(entry.accessedAtUtc) ?? "-"}</strong>
                      </div>
                      <div>
                        <label>Actor</label>
                        <strong className="wx-fsh-id-cell" title={entry.actorUserId ?? ""}>
                          {truncateId(entry.actorUserId)}
                        </strong>
                      </div>
                      <div>
                        <label>Range</label>
                        <strong>
                          {formatDateTimeTh(entry.fromUtc) ?? "-"} - {formatDateTimeTh(entry.toUtc) ?? "-"}
                        </strong>
                      </div>
                      <div>
                        <label>Points</label>
                        <strong>{entry.pointCount ?? "-"}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      )}

      <footer className="wx-fsh-about-footer">
        <span className="wx-fsh-about" tabIndex={0} title={ACCESS_LOG_ABOUT_TOOLTIP}>
          เกี่ยวกับข้อมูลนี้
        </span>
      </footer>
    </>
  );
}
